import { forwardRef } from 'react';
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Bill, BillItem } from '../../types/bill';
import { MasterRow } from '../../types/master';
import { invoiceDate } from '../../utils/dates';

interface PropsData {
  bill: Bill;
  shop: MasterRow | undefined;
}

const money = (value?: number | null) => (value === null || value === undefined ? '0.00' : Number(value).toFixed(2));

const paymentLabel: Record<string, string> = {
  CASH: 'Cash',
  UPI_CARD: 'UPI / Card',
  CREDIT: 'Credit',
  EMI: 'EMI',
  SPLIT: 'Split',
};

/**
 * The A4 bill. Rendered off-screen and handed to the printer, so what the shop
 * hands the customer is exactly what is on this page.
 */
const BillPrint = forwardRef<HTMLDivElement, PropsData>(({ bill, shop }, ref) => {
  const gstApplied = bill.gstApplied !== false;
  /**
   * Taxed as IGST, so the invoice shows one tax column instead of two.
   *
   * <p>Read from the flag the server sets rather than from the amount: a fully
   * discounted line can carry zero tax and still belong to an inter-state sale.
   */
  const interState = bill.interState === true;
  const shopName = (shop?.shopName as string) || 'Mobile Shop';
  const addressParts = [shop?.addressLine1, shop?.addressLine2, shop?.city].filter(Boolean).join(', ');

  return (
    <Box
      ref={ref}
      sx={{
        width: '210mm',
        minHeight: '297mm',
        p: '12mm',
        bgcolor: '#fff',
        color: '#000',
        fontSize: '12px',
        /**
         * Paper is white, so the bill is always black on white - the screen theme
         * has no say in it. Without this, dark mode handed MUI's own text colour to
         * every table cell and the whole item table printed white on white.
         */
        '& *': { color: '#000 !important', borderColor: '#000 !important' },
        '@media print': { p: '10mm', bgcolor: '#fff', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, letterSpacing: '.5px' }}>{shopName}</Typography>
        {addressParts && <Typography sx={{ fontSize: '12px' }}>{addressParts}</Typography>}
        {shop?.phone ? <Typography sx={{ fontSize: '12px' }}>Phone: {String(shop.phone)}</Typography> : null}
        {shop?.gstin ? <Typography sx={{ fontSize: '12px' }}>GSTIN: {String(shop.gstin)}</Typography> : null}
      </Box>

      <Divider sx={{ my: 1.5, borderColor: '#000' }} />

      <Typography sx={{ textAlign: 'center', fontWeight: 700, fontSize: '13px', mb: 1.5 }}>
        {gstApplied ? 'TAX INVOICE' : 'BILL OF SUPPLY'}
        {bill.status === 'CANCELLED' && ' — CANCELLED'}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '12px' }}>
            <b>Bill No:</b> {bill.billNo}
          </Typography>
          <Typography sx={{ fontSize: '12px' }}>
            <b>Date:</b> {invoiceDate(bill.billDate)}
          </Typography>
          {/* A GST invoice has to name the state the sale is taxed to. */}
          {gstApplied && bill.placeOfSupply && (
            <Typography sx={{ fontSize: '12px' }}>
              <b>Place of Supply:</b> {bill.placeOfSupply}
              {bill.placeOfSupplyCode ? ` (${bill.placeOfSupplyCode})` : ''}
            </Typography>
          )}
          <Typography sx={{ fontSize: '12px' }}>
            <b>Payment:</b> {paymentLabel[bill.paymentType] || bill.paymentType}
          </Typography>
          {/* A split bill says one word at the top; the customer wants to see which
              part of their money went which way. */}
          {(bill.tenders?.length ?? 0) > 1 && (
            <Typography sx={{ fontSize: '11px' }}>
              {bill.tenders!.map((tender) => `${paymentLabel[tender.paymentMode] || tender.paymentMode} ${money(tender.amount)}`).join('  ·  ')}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '12px' }}>
            <b>{bill.customerName}</b>
          </Typography>
          {bill.mobileNumber && <Typography sx={{ fontSize: '12px' }}>{bill.mobileNumber}</Typography>}
          {bill.address && <Typography sx={{ fontSize: '12px' }}>{bill.address}</Typography>}
        </Box>
      </Box>

      <Table size="small" sx={{ '& td, & th': { borderColor: '#000', fontSize: '11px', py: '4px' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>HSN</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Rate
            </TableCell>
            {gstApplied && (
              <>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Taxable
                </TableCell>
                {/* One state away and the whole rate is a single IGST charge, so the
                    invoice shows one column instead of two. */}
                {interState ? (
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    IGST
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      CGST
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      SGST
                    </TableCell>
                  </>
                )}
              </>
            )}
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {printLines(bill.items).map((line, index) => (
            <TableRow key={line.key}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                {line.description}
                {line.imeis.length > 0 && (
                  <Typography component="div" sx={{ fontSize: '10px' }}>
                    Serial No: {line.imeis.join(', ')}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{line.hsnCode || '-'}</TableCell>
              <TableCell align="center">{line.quantity}</TableCell>
              <TableCell align="right">{money(line.rate)}</TableCell>
              {gstApplied && (
                <>
                  <TableCell align="right">{money(line.taxableAmount)}</TableCell>
                  {interState ? (
                    <TableCell align="right">{money(line.igstAmount)}</TableCell>
                  ) : (
                    <>
                      <TableCell align="right">{money(line.cgstAmount)}</TableCell>
                      <TableCell align="right">{money(line.sgstAmount)}</TableCell>
                    </>
                  )}
                </>
              )}
              <TableCell align="right">{money(line.totalAmount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 3 }}>
        <Box sx={{ fontSize: '11px', maxWidth: '95mm' }}>
          {bill.emiDetails && (
            <>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 0.5 }}>EMI Details</Typography>
              <Typography sx={{ fontSize: '11px' }}>Bank: {bill.emiDetails.bankName}</Typography>
              <Typography sx={{ fontSize: '11px' }}>Down payment: {money(bill.emiDetails.downPayment)}</Typography>
              <Typography sx={{ fontSize: '11px' }}>Financed: {money(bill.emiDetails.financedAmount)}</Typography>
              {bill.emiDetails.tenureMonths ? (
                <Typography sx={{ fontSize: '11px' }}>
                  Tenure: {bill.emiDetails.tenureMonths} months &nbsp; EMI: {money(bill.emiDetails.emiAmount)}
                </Typography>
              ) : null}
              {bill.emiDetails.loanNumber && <Typography sx={{ fontSize: '11px' }}>Loan No: {bill.emiDetails.loanNumber}</Typography>}
            </>
          )}
          {/* Any bill that leaves something owing, however it was paid - not only the
              ones marked Credit, now that a split bill can carry a balance too. */}
          {Number(bill.balanceAmount) > 0 && (
            <Typography sx={{ fontSize: '11px', fontWeight: 700, mt: 1 }}>
              Paid {money(bill.paidAmount)} &nbsp;·&nbsp; Balance {money(bill.balanceAmount)}
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: '65mm' }}>
          <Row label="Taxable" value={money(bill.taxableAmount)} />
          {gstApplied &&
            (interState ? (
              <Row label="IGST" value={money(bill.igstAmount)} />
            ) : (
              <>
                <Row label="CGST" value={money(bill.cgstAmount)} />
                <Row label="SGST" value={money(bill.sgstAmount)} />
              </>
            ))}
          {Number(bill.discountAmount) > 0 && <Row label="Discount" value={money(bill.discountAmount)} />}
          <Row label="Round off" value={money(bill.roundOff)} />
          <Divider sx={{ my: 0.5, borderColor: '#000' }} />
          <Row label="TOTAL" value={money(bill.totalAmount)} bold />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6 }}>
        <Typography sx={{ fontSize: '11px' }}>Customer signature</Typography>
        <Typography sx={{ fontSize: '11px' }}>for {shopName}</Typography>
      </Box>

      <Typography sx={{ fontSize: '10px', textAlign: 'center', mt: 3 }}>
        Goods once sold are covered by the manufacturer's warranty only. Thank you for your business.
      </Typography>
    </Box>
  );
});

const Row = ({ label, value, bold }: { label: string; value: string; bold?: boolean }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: bold ? '14px' : '12px', fontWeight: bold ? 700 : 400 }}>
    <span>{label}</span>
    <span>{value}</span>
  </Box>
);

BillPrint.displayName = 'BillPrint';

/**
 * Two of the same phone at the same price is one line of two, not two lines of one.
 *
 * <p>Internally each piece is its own row - it has to be, they have different serial
 * numbers and came from different batches. A customer buying two of the same model
 * does not want to read that twice on their bill, so lines are folded together by
 * product and price, quantities added, and the serial numbers listed underneath.
 *
 * <p>Anything priced differently stays on its own line: two of the same phone at two
 * prices is a discount the customer can see, and hiding it would be wrong.
 */
function printLines(items?: BillItem[]) {
  const lines: {
    key: string;
    description?: string;
    hsnCode?: string | null;
    imeis: string[];
    quantity: number;
    rate?: number | null;
    taxableAmount: number;
    cgstAmount: number;
    sgstAmount: number;
    igstAmount: number;
    totalAmount: number;
  }[] = [];

  (items ?? []).forEach((item) => {
    const key = `${item.productId ?? item.description}|${item.rate ?? 0}|${item.discountAmount ?? 0}`;
    const existing = lines.find((line) => line.key === key);
    const target =
      existing ??
      (lines.push({
        key,
        description: item.description,
        hsnCode: item.hsnCode,
        imeis: [],
        quantity: 0,
        rate: item.rate,
        taxableAmount: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
      }),
      lines[lines.length - 1]);

    if (item.imeiNo) target.imeis.push(item.imeiNo);
    target.quantity += Number(item.quantity) || 0;
    target.taxableAmount += Number(item.taxableAmount) || 0;
    target.cgstAmount += Number(item.cgstAmount) || 0;
    target.sgstAmount += Number(item.sgstAmount) || 0;
    target.igstAmount += Number(item.igstAmount) || 0;
    target.totalAmount += Number(item.totalAmount) || 0;
  });

  return lines;
}

export default BillPrint;
